import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { SelectNative } from "../../components/ui/select-native";
import {
  deleteContactRequest,
  enrolFromContactRequest,
  listContactRequests,
  updateContactRequestStatus,
} from "../../lib/adminApi";

const STATUS_LABEL = {
  new: "New",
  contacted: "Contacted",
  enrolled: "Enrolled",
  dismissed: "Dismissed",
};

const STATUS_TONES = {
  new: "bg-gold/15 text-gold",
  contacted: "bg-sky-400/15 text-sky-300",
  enrolled: "bg-emerald-400/15 text-emerald-300",
  dismissed: "bg-muted/20 text-muted",
};

function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** Admin view for landing-page "Request Access" leads. One-click enrol. */
export function AdminRequestsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listContactRequests(filter));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load requests.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleEnrol(row) {
    if (!window.confirm(`Enrol ${row.name || row.email || row.mobile_e164} as an active member?`)) return;
    try {
      const enrolment = await enrolFromContactRequest(row.id);
      toast.success(`Enrolled — enrolment id ${enrolment.id.slice(0, 8)}…`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't enrol.");
    }
  }

  async function handleStatus(row, status) {
    try {
      await updateContactRequestStatus(row.id, status);
      toast.success(`Marked as ${STATUS_LABEL[status].toLowerCase()}.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update.");
    }
  }

  async function handleDelete(row) {
    if (!window.confirm("Delete this request permanently?")) return;
    try {
      await deleteContactRequest(row.id);
      toast.success("Request deleted.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete.");
    }
  }

  const filtered = useMemo(() => {
    if (!q) return rows;
    const needle = q.toLowerCase();
    return rows.filter((r) =>
      `${r.name || ""} ${r.email || ""} ${r.mobile_e164 || ""} ${r.message || ""}`.toLowerCase().includes(needle),
    );
  }, [rows, q]);

  return (
    <div className="flex flex-col gap-6" data-testid="admin-requests-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-gold">Requests</p>
        <h1 className="mt-1 text-3xl font-semibold text-text">Access requests</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted">
          Leads submitted via the public landing page&apos;s &ldquo;Request Access&rdquo; form. One click
          turns a lead into an active enrolment.
        </p>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="flex flex-col gap-3 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <SelectNative
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-40"
              data-testid="requests-status-filter"
            >
              <option value="">All statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="enrolled">Enrolled</option>
              <option value="dismissed">Dismissed</option>
            </SelectNative>
            <span className="text-sm text-muted">{filtered.length} request{filtered.length === 1 ? "" : "s"}</span>
          </div>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name / email / mobile / message"
            className="w-full sm:w-72"
            data-testid="requests-search"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-[10px] uppercase tracking-widest text-muted">
              <tr>
                <th className="px-4 py-3">Person</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Message</th>
                <th className="px-4 py-3">Received</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-muted">Loading…</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-muted">
                    No requests match — invite people to your landing page to see leads here.
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-border/70 last:border-b-0 hover:bg-elevated/60"
                    data-testid={`request-row-${r.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="text-text">{r.name || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-text">{r.email || "—"}</div>
                      <div className="font-mono text-[11px] text-muted">{r.mobile_e164 || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="max-w-sm truncate text-xs text-muted" title={r.message || ""}>
                        {r.message || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted">{formatDateTime(r.created_at)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                          STATUS_TONES[r.status] || STATUS_TONES.new
                        }`}
                      >
                        {STATUS_LABEL[r.status] || r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        {r.status !== "enrolled" ? (
                          <Button
                            size="sm"
                            onClick={() => handleEnrol(r)}
                            data-testid={`enrol-${r.id}`}
                          >
                            Enrol
                          </Button>
                        ) : (
                          <span className="text-[10px] font-mono text-muted">
                            #{r.enrolment_id?.slice(0, 8) || ""}
                          </span>
                        )}
                        {r.status === "new" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStatus(r, "contacted")}
                            data-testid={`contacted-${r.id}`}
                          >
                            Mark contacted
                          </Button>
                        )}
                        {r.status !== "dismissed" && r.status !== "enrolled" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStatus(r, "dismissed")}
                            data-testid={`dismiss-${r.id}`}
                          >
                            Dismiss
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(r)}
                          data-testid={`delete-${r.id}`}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminRequestsPage;
